/* eslint-disable react/style-prop-object */
import React, { useEffect } from 'react';
import { Tooltip } from 'antd';
import type { LngLatBoundsLike } from 'react-map-gl';
import Map, { Source, Layer, Marker, NavigationControl } from 'react-map-gl';
import { getBounds, markerForIndex } from './markerUtils';
import WebMercatorViewport from '@math.gl/web-mercator';
import { palette } from '@shipium-corp/shipium-ui-theme';

export const USDefaultBounds: LngLatBoundsLike = [
  {
    lng: -128.2421875,
    lat: 19.972741,
  },
  {
    lng: -65.63671875,
    lat: 49.696361,
  },
];

export interface SimpleMapMarker {
  latitude: number;
  longitude: number;
  name: string;
  tooltip: React.ReactNode | 'string';
  colorIndex?: number;
}

export interface SimpleMapProps {
  fitToMarkers?: boolean;
  markers?: SimpleMapMarker[];
  maxBounds?: LngLatBoundsLike;
  label?: string;
  legendHeader?: React.ReactNode;
  legendPosition?: 'left' | 'right';
  disableDrag?: boolean;
  disableZoom?: boolean;
}

const MAX_MARKERS_FOR_CLUSTERING = 100;

interface Viewport {
  longitude: number;
  latitude: number;
  zoom: number;
}

interface HoverInfo {
  x: number;
  y: number;
  count: number;
}

export const SimpleMap = React.memo((props: SimpleMapProps) => {
  const { markers, disableDrag, disableZoom, maxBounds, fitToMarkers } = props;
  const [viewport, setViewport] = React.useState<Viewport>();
  const useClusters = (markers?.length || 0) > MAX_MARKERS_FOR_CLUSTERING;
  const [hoverInfo, setHoverInfo] = React.useState<HoverInfo | null>(null);

  useEffect(() => {
    if (markers && fitToMarkers) {
      // TODO investigate why these width + height values need to be set
      const vp = new WebMercatorViewport({ width: 800, height: 600 });
      const { longitude, latitude, zoom } = vp.fitBounds(getBounds(markers), {
        padding: 100,
      });
      setViewport({ longitude, latitude, zoom: zoom });
    }
  }, [fitToMarkers, markers]);

  const mapMarkers =
    markers?.map((marker, index) => {
      const iconUrl = markerForIndex(marker.colorIndex ?? index);
      return (
        <Marker
          longitude={marker.longitude}
          latitude={marker.latitude}
          key={`zip3choropleth-marker-${index}`}
          anchor="bottom"
        >
          <Tooltip placement="topLeft" title={marker.tooltip}>
            <img src={iconUrl} alt={marker.name} />
          </Tooltip>
        </Marker>
      );
    }) || [];

  if (disableZoom !== true) {
    mapMarkers.push(<NavigationControl showCompass={false} />);
  }

  const geojson = {
    type: 'FeatureCollection' as const,
    features: (markers || []).map((marker) => ({
      type: 'Feature' as const,
      properties: {
        name: marker.name,
        description: typeof marker.tooltip === 'string' ? marker.tooltip : marker.name,
      },
      geometry: {
        type: 'Point' as const,
        coordinates: [marker.longitude, marker.latitude],
      },
    })),
  };

  return (
    <div>
      <Map
        {...(fitToMarkers ? viewport : {})}
        initialViewState={{
          zoom: 3.5,
          bounds: USDefaultBounds,
        }}
        scrollZoom={!disableZoom}
        dragPan={!disableDrag}
        dragRotate={false}
        maxZoom={3.5}
        cursor={'default'}
        mapStyle={`mapbox://styles/mapbox/light-v10`}
        maxBounds={maxBounds}
        mapboxAccessToken={
          'pk.eyJ1IjoibWFjLXNoaXBpdW0iLCJhIjoiY2tkb29jbTBuMGE0eTJybzJzdWFucW9weSJ9.Zk9IJNw95wjvjqGatn8OFw'
        }
        style={{
          height: '65vh',
        }}
        onMouseMove={(event) => {
          const feature = event.features?.find((f) => f.layer.id);
          if (feature) {
            setHoverInfo({
              x: event.point.x,
              y: event.point.y,
              count: feature?.properties?.point_count ?? 1,
            });
          } else {
            setHoverInfo(null);
          }
        }}
        interactiveLayerIds={['clusters']}
      >
        {useClusters ? (
          <>
            <Source
              id="fulfillmentMarkers"
              type="geojson"
              data={geojson}
              cluster={true}
              clusterMaxZoom={10}
              clusterRadius={25}
            >
              <Layer
                id="clusters"
                type="circle"
                source="fulfillmentMarkers"
                filter={['has', 'point_count']}
                paint={{
                  'circle-color': palette.Teal.light,
                  'circle-radius': ['step', ['get', 'point_count'], 20, 100, 30, 750, 40],
                  'circle-stroke-width': 1,
                  'circle-stroke-color': '#fff',
                }}
              />
              <Layer
                id="cluster-count"
                type="symbol"
                source="fulfillmentMarkers"
                filter={['has', 'point_count']}
                layout={{
                  'text-field': '{point_count_abbreviated}',
                  'text-font': ['DIN Offc Pro Medium', 'Arial Unicode MS Bold'],
                  'text-size': 14,
                }}
                paint={{
                  'text-color': '#000',
                }}
              />
              <Layer
                id="unclustered-point"
                type="circle"
                source="fulfillmentMarkers"
                filter={['!', ['has', 'point_count']]}
                paint={{
                  'circle-color': palette.Teal.light,
                  'circle-radius': 12,
                  'circle-stroke-width': 1,
                  'circle-stroke-color': '#fff',
                }}
              />
              <Layer
                id="unclustered-point-label"
                type="symbol"
                source="fulfillmentMarkers"
                filter={['!', ['has', 'point_count']]}
                layout={{
                  'text-field': '1',
                  'text-font': ['DIN Offc Pro Medium', 'Arial Unicode MS Bold'],
                  'text-size': 14,
                  'text-offset': [0, 0.1],
                  'text-anchor': 'center',
                }}
                paint={{
                  'text-color': '#000',
                }}
              />
            </Source>
            {hoverInfo && (
              <div
                style={{
                  position: 'absolute',
                  left: hoverInfo.x,
                  top: hoverInfo.y,
                  background: palette.Grey.darkest,
                  padding: '4px 8px',
                  border: `1px solid ${palette.Grey.darkest}`,
                  borderRadius: 4,
                  pointerEvents: 'none',
                  transform: 'translate(-50%, -100%)',
                  color: 'white',
                }}
              >
                {hoverInfo.count} {hoverInfo.count === 1 ? 'location' : 'locations'}
              </div>
            )}
          </>
        ) : (
          mapMarkers
        )}
      </Map>
    </div>
  );
});

SimpleMap.displayName = 'SimpleMap';
